// COMPLETE FILE - RENDER COMPATIBLE
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import WebcamCapture from '../components/WebcamCapture';

const FaceVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('checking');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);

  // Render-compatible API URL
  const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://travel-friend-backend.onrender.com'
    : 'http://localhost:5000';

  // Hide header when component mounts
  useEffect(() => {
    const hideElements = [
      'header', 'nav', '.navbar', '.header', '.app-header',
      '[class*="header"]', '[class*="navbar"]',
      '[class*="Header"]', '[class*="Navbar"]'
    ];
    
    hideElements.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.style.display = 'none';
      });
    });

    checkVerificationStatus();

    return () => {
      hideElements.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.style.display = '';
        });
      });
    };
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }

      console.log('🔑 Checking verification status...');
      
      const response = await axios.get(`${API_BASE_URL}/api/face-verification/status`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        console.log('🔐 Verification status:', response.data);
        
        if (response.data.lockedUntil) {
          const lockUntil = new Date(response.data.lockedUntil);
          if (lockUntil > new Date()) {
            setIsLocked(true);
            setLockTime(lockUntil);
            setStep('locked');
            return;
          }
        }
        
        setAttempts(response.data.attempts || 0);
        
        if (!response.data.hasProfileImage) {
          setStep('upload');
        } else if (!response.data.isFaceVerified) {
          setStep('verify');
        } else {
          const intendedPath = location.state?.intendedPath || '/dashboard';
          navigate(intendedPath);
        }
      }
    } catch (error) {
      console.error('❌ Status check failed:', error);
      
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please login again.');
        navigate('/login');
      } else if (error.response?.status === 404) {
        console.log('ℹ️ Face verification not available, proceeding to dashboard');
        const intendedPath = location.state?.intendedPath || '/dashboard';
        navigate(intendedPath);
      } else {
        setError('Unable to check verification status. Please try again.');
        setStep('error');
      }
    }
  };

  const handleProfileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login again');
        navigate('/login');
        return;
      }

      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await axios.post(
        `${API_BASE_URL}/api/face-verification/upload-profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Profile photo uploaded successfully!');
        setStep('verify');
      }

    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to upload profile photo. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLiveCapture = async (imageData) => {
    setIsProcessing(true);
    setError('');

    try {
      const blob = await fetch(imageData).then(res => res.blob());
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('liveImage', file);

      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_BASE_URL}/api/face-verification/verify`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.data.success && response.data.verified) {
        toast.success('Face verification successful!');
        setStep('success');
        
        setTimeout(() => {
          const intendedPath = location.state?.intendedPath || '/dashboard';
          navigate(intendedPath);
        }, 2000);
      } else {
        const newAttempts = response.data.attempts || attempts + 1;
        setAttempts(newAttempts);
        
        const errorMsg = response.data.message || 'Verification failed. Please try again.';
        setError(errorMsg);
        toast.error(errorMsg);
        
        if (response.data.lockedUntil) {
          setIsLocked(true);
          setLockTime(new Date(response.data.lockedUntil));
          setStep('locked');
        }
      }
    } catch (error) {
      console.error('❌ Verification error:', error);
      
      let errorMsg = 'Verification failed. Please try again.';
      if (error.response?.status === 429) {
        errorMsg = 'Too many attempts. Please wait and try again.';
        if (error.response.data.lockedUntil) {
          setIsLocked(true);
          setLockTime(new Date(error.response.data.lockedUntil));
          setStep('locked');
        }
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.code === 'ERR_NETWORK') {
        errorMsg = 'Cannot connect to our servers. Please check your internet connection.';
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStep = () => {
    if (isLocked && lockTime) {
      const now = new Date();
      const diffMs = lockTime - now;
      const diffMins = Math.ceil(diffMs / 60000);
      
      return (
        <div className="text-center p-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-red-100 to-pink-100 mb-6 border border-red-200/50">
            <span className="text-4xl">🔒</span>
          </div>
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Account Temporarily Locked</h2>
          <p className="text-gray-600 mb-6 max-w-sm mx-auto">
            For security reasons, your account has been temporarily locked due to multiple failed verification attempts.
          </p>
          <div className="bg-gradient-to-r from-red-50 to-pink-50/50 rounded-xl p-4 mb-8 border border-red-200/50">
            <p className="text-red-600 font-medium">
              You can try again in <span className="text-lg font-bold">{diffMins}</span> minute{diffMins !== 1 ? 's' : ''}
            </p>
          </div>
          <button 
            onClick={() => {
              if (new Date() >= lockTime) {
                setIsLocked(false);
                setStep('checking');
                checkVerificationStatus();
              }
            }}
            className="w-full py-4 px-6 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium hover:from-gray-700 hover:to-gray-800 transition-all active:scale-[0.98] shadow-lg shadow-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={new Date() < lockTime}
          >
            {new Date() < lockTime ? 'Please wait...' : 'Try Again'}
          </button>
        </div>
      );
    }

    switch (step) {
      case 'checking':
        return (
          <div className="text-center p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Checking Verification Status</h3>
            <p className="text-gray-600">Please wait while we verify your account...</p>
          </div>
        );

      case 'upload':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-3 text-gray-800">Setup Face Verification</h2>
              <p className="text-gray-600">
                Upload a clear photo of your face for enhanced security during future logins.
              </p>
            </div>
            
            <div className="space-y-4">
              <label className="block">
                <div className={`
                  relative border-3 border-dashed rounded-2xl p-8 text-center transition-all duration-300
                  ${isProcessing 
                    ? 'border-gray-300 bg-gray-50' 
                    : 'border-blue-200 bg-gradient-to-br from-blue-50/50 to-purple-50/50 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-100'
                  }
                `}>
                  {isProcessing ? (
                    <div className="space-y-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/80">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Processing your photo...</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100">
                        <span className="text-3xl">📷</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Select a Photo</p>
                        <p className="text-sm text-gray-500 mt-1">JPEG or PNG • Max 5MB</p>
                      </div>
                      <div className="pt-2">
                        <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg">
                          Choose File
                        </span>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleProfileUpload}
                    disabled={isProcessing}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </label>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 rounded-2xl p-5 border border-blue-100/50">
              <h4 className="font-bold mb-3 text-gray-800 flex items-center">
                <span className="mr-2">📋</span>
                Photo Requirements
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">✓</span>
                  <span>Clear, well-lit face photo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">✓</span>
                  <span>Look directly at the camera</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">✓</span>
                  <span>No sunglasses or hats</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">✓</span>
                  <span>Only one person in the photo</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">✓</span>
                  <span>No blurry or dark images</span>
                </li>
              </ul>
            </div>
          </div>
        );

      case 'verify':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-3 text-gray-800">Verify Your Identity</h2>
              <p className="text-gray-600">
                Take a live selfie to verify it's really you accessing the account.
              </p>
            </div>
            
            {attempts > 0 && (
              <div className={`
                rounded-xl p-4 font-medium text-center transition-all
                ${attempts >= 2 
                  ? 'bg-gradient-to-r from-yellow-50 to-orange-50/50 text-yellow-800 border border-yellow-200/50 animate-pulse' 
                  : 'bg-gradient-to-r from-blue-50 to-purple-50/50 text-blue-800 border border-blue-200/50'
                }
              `}>
                <div className="flex items-center justify-center">
                  <span className="mr-2">⚠️</span>
                  <span>Attempt {attempts} of 3</span>
                </div>
                {attempts >= 2 && (
                  <p className="text-sm mt-1 font-semibold">Last attempt before temporary lockout</p>
                )}
              </div>
            )}
            
            <WebcamCapture 
              onCapture={handleLiveCapture}
              isCapturing={isProcessing}
            />
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 rounded-2xl p-5 border border-blue-100/50">
                <h4 className="font-bold mb-3 text-gray-800 flex items-center">
                  <span className="mr-2">💡</span>
                  Tips for Success
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Good lighting</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Center your face</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Neutral expression</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Remove glasses</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Hold still</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Same hairstyle</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-2xl p-5 border border-green-200/50">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center mr-3">
                    <span className="text-green-600">🔒</span>
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-800 mb-1">Your Security Matters</h5>
                    <p className="text-sm text-gray-600">
                      Your face data is encrypted and never shared with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center p-4">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 mb-6 animate-bounce">
              <span className="text-5xl text-green-500">✓</span>
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">Verification Successful!</h2>
            <p className="text-gray-600 mb-8 max-w-sm mx-auto">
              Your identity has been verified. You'll be redirected to your dashboard shortly.
            </p>
            <div className="max-w-xs mx-auto">
              <div className="h-2 bg-gradient-to-r from-green-200 to-emerald-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-[progress_2s_ease-in-out]"></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Redirecting...</p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-red-100 to-pink-100 mb-2">
              <span className="text-4xl text-red-500">✗</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-3 text-gray-800">Verification Failed</h2>
              <div className="bg-gradient-to-r from-red-50 to-pink-50/50 rounded-xl p-4 mb-6 border border-red-200/50">
                <p className="text-red-600 font-medium">{error}</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <button 
                onClick={() => {
                  setError('');
                  setStep('checking');
                  checkVerificationStatus();
                }}
                className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all active:scale-[0.98] shadow-lg shadow-blue-200"
              >
                Try Again
              </button>
              
              <button 
                onClick={() => navigate('/login')}
                className="w-full py-4 px-6 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl font-medium hover:from-gray-200 hover:to-gray-300 transition-all active:scale-[0.98]"
              >
                Back to Login
              </button>
              
              <button 
                onClick={() => navigate('/dashboard')}
                className="w-full py-4 px-6 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-xl font-medium hover:from-green-100 hover:to-emerald-100 transition-all active:scale-[0.98] border border-green-200"
              >
                Skip to Dashboard
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 flex items-center justify-center p-4 md:p-6">
      <div className="w-full max-w-md">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl shadow-blue-200/30 overflow-hidden border border-white/30">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white pt-10 pb-8 px-6 text-center overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              <div className="absolute -top-20 -left-20 w-40 h-40 rounded-full bg-white/10"></div>
              <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-white/10"></div>
            </div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 mb-6">
                <span className="text-4xl">🔒</span>
              </div>
              
              <h1 className="text-2xl font-bold mb-2 tracking-tight">Security Verification</h1>
              <p className="opacity-95 text-sm font-medium tracking-wide">
                Additional verification required for account security
              </p>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="px-6 py-8">
            {renderStep()}
          </div>
          
          {/* Help Section */}
          {step !== 'success' && step !== 'checking' && step !== 'locked' && step !== 'error' && (
            <div className="border-t border-gray-100 pt-6 pb-8 px-6 text-center">
              <div className="inline-flex items-center justify-center text-sm text-gray-600 hover:text-blue-600 transition-colors cursor-pointer group">
                <span className="mr-2">Need assistance?</span>
                <button 
                  onClick={() => navigate('/help')}
                  className="text-blue-600 hover:text-blue-800 font-medium group-hover:underline"
                >
                  Contact Support
                </button>
                <span className="ml-1 group-hover:translate-x-0.5 transition-transform">→</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaceVerification;